const DB_NAME = "plate-os-locales";
const STORE_NAME = "locales";
const META_STORE_NAME = "meta";
const DB_VERSION = 2;
const LRU_KEY = "lru";
const FALLBACK_LANG = "en";

/**
 * Maximum number of locale blobs retained in IndexedDB.
 * The active language and the fallback ("en") are always protected, so the
 * actual count may reach MAX_CACHED_LOCALES + 1 when both are outside the
 * normal top-N window.
 */
export const MAX_CACHED_LOCALES = 5;

let dbPromise: Promise<IDBDatabase> | null = null;

function openDB(): Promise<IDBDatabase> {
  if (dbPromise) return dbPromise;
  dbPromise = new Promise<IDBDatabase>((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME);
      }
      if (!db.objectStoreNames.contains(META_STORE_NAME)) {
        db.createObjectStore(META_STORE_NAME);
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => {
      dbPromise = null;
      reject(req.error);
    };
  });
  return dbPromise;
}

function langFromKey(key: string, appVersion: string): string | null {
  const suffix = `@${appVersion}`;
  return key.endsWith(suffix) ? key.slice(0, -suffix.length) : null;
}

function moveFront(list: string[], lang: string): string[] {
  return [lang, ...list.filter((l) => l !== lang)];
}

function reconcile(lruList: string[], storedLangs: Set<string>): string[] {
  const inLru = new Set(lruList);
  const extra = [...storedLangs].filter((l) => !inLru.has(l));
  return [...lruList.filter((l) => storedLangs.has(l)), ...extra];
}

function computeEvictions(
  ordered: string[],
  protectedLangs: ReadonlySet<string>,
  max: number,
): string[] {
  const evicted: string[] = [];
  let kept = 0;
  for (const lang of ordered) {
    if (kept < max || protectedLangs.has(lang)) {
      kept++;
    } else {
      evicted.push(lang);
    }
  }
  return evicted;
}

function touchLru(
  db: IDBDatabase,
  lang: string,
  appVersion: string,
): Promise<void> {
  return new Promise<void>((resolve) => {
    const tx = db.transaction([STORE_NAME, META_STORE_NAME], "readwrite");
    const localeStore = tx.objectStore(STORE_NAME);
    const metaStore = tx.objectStore(META_STORE_NAME);

    const keysReq = localeStore.getAllKeys();
    keysReq.onsuccess = () => {
      const storedLangs = new Set(
        (keysReq.result as string[])
          .map((k) => langFromKey(k, appVersion))
          .filter((l): l is string => l !== null),
      );

      const lruReq = metaStore.get(LRU_KEY);
      lruReq.onsuccess = () => {
        const rawLru = (lruReq.result as string[] | undefined) ?? [];
        const reconciled = reconcile(rawLru, storedLangs);
        const ordered = moveFront(reconciled, lang);

        const protected_ = new Set([lang, FALLBACK_LANG]);
        const toEvict = computeEvictions(ordered, protected_, MAX_CACHED_LOCALES);
        const kept = ordered.filter((l) => !toEvict.includes(l));

        for (const evicted of toEvict) {
          localeStore.delete(`${evicted}@${appVersion}`);
        }
        metaStore.put(kept, LRU_KEY);
      };
    };

    tx.oncomplete = () => resolve();
    tx.onerror = () => resolve();
  });
}

export async function getCachedLocale(
  lang: string,
  appVersion: string,
): Promise<Record<string, unknown> | null> {
  try {
    const db = await openDB();
    const key = `${lang}@${appVersion}`;
    const result = await new Promise<Record<string, unknown> | null>(
      (resolve) => {
        const tx = db.transaction(STORE_NAME, "readonly");
        const req = tx.objectStore(STORE_NAME).get(key);
        req.onsuccess = () =>
          resolve((req.result as Record<string, unknown>) ?? null);
        req.onerror = () => resolve(null);
      },
    );

    if (result !== null) {
      void touchLru(db, lang, appVersion);
    }

    return result;
  } catch {
    return null;
  }
}

export async function setCachedLocale(
  lang: string,
  appVersion: string,
  data: Record<string, unknown>,
): Promise<void> {
  try {
    const db = await openDB();
    const key = `${lang}@${appVersion}`;
    await new Promise<void>((resolve) => {
      const tx = db.transaction(STORE_NAME, "readwrite");
      tx.objectStore(STORE_NAME).put(data, key);
      tx.oncomplete = () => resolve();
      tx.onerror = () => resolve();
    });
    await touchLru(db, lang, appVersion);
  } catch {
    // Ignore cache write failures — the app still functions without the cache.
  }
}

export async function evictStaleLocales(
  currentAppVersion: string,
): Promise<void> {
  try {
    const db = await openDB();
    await new Promise<void>((resolve) => {
      const tx = db.transaction([STORE_NAME, META_STORE_NAME], "readwrite");
      const localeStore = tx.objectStore(STORE_NAME);
      const metaStore = tx.objectStore(META_STORE_NAME);

      const keysReq = localeStore.getAllKeys();
      keysReq.onsuccess = () => {
        const allKeys = keysReq.result as string[];
        for (const key of allKeys) {
          if (!key.endsWith(`@${currentAppVersion}`)) {
            localeStore.delete(key);
          }
        }

        const survivingLangs = new Set(
          allKeys
            .map((k) => langFromKey(k, currentAppVersion))
            .filter((l): l is string => l !== null),
        );

        const lruReq = metaStore.get(LRU_KEY);
        lruReq.onsuccess = () => {
          const rawLru = (lruReq.result as string[] | undefined) ?? [];
          const pruned = rawLru.filter((l) => survivingLangs.has(l));
          metaStore.put(pruned, LRU_KEY);
        };
      };

      tx.oncomplete = () => resolve();
      tx.onerror = () => resolve();
    });
  } catch {
    // Ignore eviction failures — stale entries are harmless.
  }
}
